
import { Component, Prop, Vue } from 'vue-property-decorator';
import { SpeechStatus, speechErrorMessage } from '@/api/dataSource/api/Spirit/Admin/Services/SpeechService';

@Component
export default class SpeechGenerationPanel extends Vue {
  @Prop({ type: Number, default: 0 }) affirmationId: number;
  @Prop({ type: Boolean, default: false }) disabled: boolean;
  @Prop({ type: String, default: '' }) disabledReason: string;
  status: SpeechStatus = null;
  statusReady = false;
  statusLoading = false;
  actionLoading = false;
  cancelling = false;
  statusError = '';
  actionError = '';
  actionMessage = '';
  ownsTask = false;
  showErrors = false;
  private pollTimer: number = null;
  private disposed = false;
  private statusRequest: Promise<void> = null;

  get running(): boolean { return !!(this.status && this.status.isRunning); }
  get showProgress(): boolean {
    return !!(this.status && (this.running || this.ownsTask && (this.status.finished || this.status.isCancelled || this.status.total)));
  }
  get progressPercent(): number {
    return this.status && this.status.total ? Math.min(100, Math.round(this.status.processed * 100 / this.status.total)) : 0;
  }
  get statusLabel(): string {
    if (this.running) return this.cancelling || this.status.isCancelled ? 'Завершаем начатые фразы' : 'Создаём озвучку';
    if (this.status.isCancelled) return 'Озвучка остановлена';
    return this.status.failed ? 'Озвучка завершена с ошибками' : 'Озвучка завершена';
  }
  created() { void this.refreshStatus(); }
  beforeDestroy() {
    this.disposed = true;
    this.stopPolling();
    this.$emit('busy', false);
  }
  stopPolling() {
    if (this.pollTimer !== null) window.clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }
  applyStatus(status: SpeechStatus) {
    const wasRunning = this.running;
    this.status = status;
    this.statusReady = true;
    this.$emit('busy', status.isRunning || this.actionLoading);
    if (!status.isRunning) this.cancelling = false;
    if (wasRunning && !status.isRunning && (status.finished || status.isCancelled)) this.$emit('settled', status);
    if (wasRunning && !status.isRunning && !status.isCancelled && status.finished) this.$emit('completed', status);
  }
  private async readStatus(): Promise<void> {
    try {
      const status = await this.$api.SpeechService.getStatusAsync();
      if (this.disposed) return;
      this.applyStatus(status);
      this.statusError = '';
    } catch (error) {
      if (!this.disposed) this.statusError = speechErrorMessage(error);
    }
  }
  async refreshStatus(): Promise<void> {
    if (this.disposed) return;
    if (this.statusRequest) return this.statusRequest;
    this.stopPolling();
    this.statusLoading = true;
    this.statusRequest = this.readStatus();
    try { await this.statusRequest; }
    finally {
      this.statusRequest = null;
      this.statusLoading = false;
      if (!this.disposed && this.running && !this.actionLoading) {
        this.pollTimer = window.setTimeout(() => { void this.refreshStatus(); }, 2500);
      }
    }
  }
  async startGeneration(): Promise<void> {
    if (this.disposed || this.disabled || !(this.affirmationId > 0) || this.running || this.actionLoading || !this.statusReady) return;
    this.actionLoading = true;
    this.$emit('busy', true);
    this.stopPolling();
    this.actionMessage = '';
    this.actionError = '';
    this.showErrors = false;
    try {
      if (this.statusRequest) await this.statusRequest;
      if (this.disposed || this.running) return;
      this.$emit('starting');
      const status = await this.$api.SpeechService.generateAllAsync({
        affirmationIds: [this.affirmationId],
        langCodes: [],
        force: false,
      });
      if (this.disposed) return;
      this.ownsTask = true;
      this.cancelling = false;
      this.applyStatus(status);
      this.actionMessage = 'Озвучка запущена';
    } catch (error) {
      if (this.disposed) return;
      if (error && error.status === 409) {
        this.ownsTask = false;
        this.actionMessage = speechErrorMessage(error);
      } else this.actionError = speechErrorMessage(error);
    } finally {
      this.actionLoading = false;
      if (!this.disposed) await this.refreshStatus();
      if (!this.disposed) this.$emit('busy', this.running);
    }
  }
  async cancelGeneration(): Promise<void> {
    if (this.disposed || !this.running || this.actionLoading || this.cancelling || this.status.isCancelled) return;
    this.actionLoading = true;
    this.stopPolling();
    this.actionError = '';
    try {
      if (this.statusRequest) await this.statusRequest;
      if (this.disposed || !this.running) return;
      await this.$api.SpeechService.cancelAsync();
      if (this.disposed) return;
      this.cancelling = true;
      this.actionMessage = 'Остановка запрошена. Начатые фразы будут дообработаны.';
    } catch (error) {
      if (!this.disposed) this.actionError = speechErrorMessage(error);
    } finally {
      this.actionLoading = false;
      if (!this.disposed) await this.refreshStatus();
    }
  }
  formatError(error: any): string {
    if (typeof error === 'string') return error;
    return error && (error.message || error.error) || JSON.stringify(error);
  }
}
